import React, { useState, useEffect, memo } from 'react';

export type CardFieldType = 'name' | 'title' | 'company' | 'email' | 'phone' | 'source' | 'none';

interface CardCustomizerProps {
  onClose: () => void;
  onChange: (order: CardFieldType[]) => void;
  onNameFormatChange?: (format: string) => void;
  storagePrefix?: string;
}

const CardCustomizer = memo<CardCustomizerProps>(({ onClose, onChange, onNameFormatChange, storagePrefix = '' }) => {
  const [order, setOrder] = useState<CardFieldType[]>(() => {
    try {
      const key = storagePrefix ? `${storagePrefix}.card.customOrder` : 'card.customOrder';
      const saved = localStorage.getItem(key);
      if (saved) return JSON.parse(saved) as CardFieldType[];
    } catch {}
    return ['name', 'title', 'company', 'email', 'phone', 'source'];
  });

  const [lastFirstFormat, setLastFirstFormat] = useState(() => {
    try {
      const key = storagePrefix ? `${storagePrefix}.card.lastFirstFormat` : 'card.lastFirstFormat';
      const saved = localStorage.getItem(key);
      return saved === 'true';
    } catch {}
    return false;
  });

  const fieldOptions: { value: CardFieldType; label: string }[] = [
    { value: 'none', label: 'None' },
    { value: 'name', label: 'Name' },
    { value: 'title', label: 'Title' },
    { value: 'company', label: 'Company' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'source', label: 'Source' },
  ];

  const handleFieldChange = (index: number, value: CardFieldType) => {
    // Check if this value is already used in another dropdown (except 'none')
    const isAlreadyUsed = order.some((field, idx) => idx !== index && field === value && value !== 'none');
    
    const newOrder = [...order];
    newOrder[index] = value;
    setOrder(newOrder);
    
    if (!isAlreadyUsed) {
      // Save to localStorage
      try {
        const key = storagePrefix ? `${storagePrefix}.card.customOrder` : 'card.customOrder';
        localStorage.setItem(key, JSON.stringify(newOrder));
      } catch (error) {
        console.error('Failed to save card order:', error);
      }
      
      // Notify parent
      onChange(newOrder);
    }
  };

  const handleApply = () => {
    try {
      const orderKey = storagePrefix ? `${storagePrefix}.card.customOrder` : 'card.customOrder';
      const formatKey = storagePrefix ? `${storagePrefix}.card.lastFirstFormat` : 'card.lastFirstFormat';
      localStorage.setItem(orderKey, JSON.stringify(order));
      localStorage.setItem(formatKey, String(lastFirstFormat));
    } catch (error) {
      console.error('Failed to save card settings:', error);
    }
    onChange(order);
    if (onNameFormatChange) {
      onNameFormatChange(lastFirstFormat ? 'lastFirst' : 'firstLast');
    }
    onClose();
  };

  const handleReset = () => {
    const defaultOrder: CardFieldType[] = ['name', 'title', 'company', 'email', 'phone', 'source'];
    setOrder(defaultOrder);
    setLastFirstFormat(false);
    try {
      const orderKey = storagePrefix ? `${storagePrefix}.card.customOrder` : 'card.customOrder';
      const formatKey = storagePrefix ? `${storagePrefix}.card.lastFirstFormat` : 'card.lastFirstFormat';
      localStorage.setItem(orderKey, JSON.stringify(defaultOrder));
      localStorage.setItem(formatKey, 'false');
    } catch (error) {
      console.error('Failed to save card settings:', error);
    }
    onChange(defaultOrder);
    if (onNameFormatChange) {
      onNameFormatChange('firstLast');
    }
  };

  const handleFormatChange = (checked: boolean) => {
    setLastFirstFormat(checked);
    try {
      const key = storagePrefix ? `${storagePrefix}.card.lastFirstFormat` : 'card.lastFirstFormat';
      localStorage.setItem(key, String(checked));
    } catch (error) {
      console.error('Failed to save format setting:', error);
    }
    if (onNameFormatChange) {
      onNameFormatChange(checked ? 'lastFirst' : 'firstLast');
    }
  };

  // Ensure we have 6 dropdowns
  const dropdowns = Array.from({ length: 6 }, (_, i) => i);

  return (
    <div className="absolute z-50 top-0 left-0 right-0 mx-auto max-w-sm bg-white border-2 border-blue-500 rounded-lg shadow-2xl p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Customize Card View</h3>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 transition-colors"
          aria-label="Close customizer"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <div className="space-y-3">
        {dropdowns.map((index) => (
          <div key={index} className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 w-20">
              Field {index + 1}:
            </label>
            <select
              value={order[index] || 'none'}
              onChange={(e) => handleFieldChange(index, e.target.value as CardFieldType)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={`Select field ${index + 1}`}
              title={`Select field ${index + 1}`}
            >
              {fieldOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {/* Name Format Option */}
      <div className="flex items-center mt-4 pt-4 border-t border-gray-200">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={lastFirstFormat}
            onChange={(e) => handleFormatChange(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">
            Format: Last Name, First Name
          </span>
        </label>
      </div>

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
        <button
          onClick={handleReset}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
        >
          Reset to Default
        </button>
        <button
          onClick={handleApply}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
        >
          Apply
        </button>
      </div>
    </div>
  );
});

CardCustomizer.displayName = 'CardCustomizer';

export default CardCustomizer;

